import { useState, useEffect, useCallback } from 'react';
import { AlertTriangle, CheckCircle2, XCircle, Info, X } from 'lucide-react';

/* ═══════════════════════════════════════════════════════════
   Lightweight Toast Notification System
   - No context/provider needed — uses pub-sub pattern
   - Import showToast() anywhere to trigger notifications
   - Mount <ToastContainer /> once in App.tsx or layout
   ═══════════════════════════════════════════════════════════ */

export type ToastType = 'success' | 'error' | 'warning' | 'info';

interface ToastItem {
  id: number;
  message: string;
  type: ToastType;
  duration: number;
}

type Listener = (toast: ToastItem) => void;
const listeners: Set<Listener> = new Set();
let nextId = 1;

/** Fire a toast from anywhere — no hooks required */
export function showToast(message: string, type: ToastType = 'info', duration = 4000) {
  const item: ToastItem = { id: nextId++, message, type, duration };
  listeners.forEach((fn) => fn(item));
}

const ICON: Record<ToastType, React.ReactNode> = {
  success: <CheckCircle2 className="w-4 h-4 text-green-500 flex-shrink-0" />,
  error:   <XCircle className="w-4 h-4 text-red-500 flex-shrink-0" />,
  warning: <AlertTriangle className="w-4 h-4 text-amber-500 flex-shrink-0" />,
  info:    <Info className="w-4 h-4 text-blue-500 flex-shrink-0" />,
};

const BG: Record<ToastType, string> = {
  success: 'bg-green-50 border-green-200 text-green-800',
  error:   'bg-red-50 border-red-200 text-red-800',
  warning: 'bg-amber-50 border-amber-200 text-amber-800',
  info:    'bg-blue-50 border-blue-200 text-blue-800',
};

export function ToastContainer() {
  const [toasts, setToasts] = useState<ToastItem[]>([]);

  useEffect(() => {
    const handler: Listener = (t) => setToasts((prev) => [...prev, t]);
    listeners.add(handler);
    return () => { listeners.delete(handler); };
  }, []);

  const dismiss = useCallback((id: number) => {
    setToasts((prev) => prev.filter((t) => t.id !== id));
  }, []);

  useEffect(() => {
    if (toasts.length === 0) return;
    const oldest = toasts[0];
    const timer = setTimeout(() => dismiss(oldest.id), oldest.duration);
    return () => clearTimeout(timer);
  }, [toasts, dismiss]);

  if (toasts.length === 0) return null;

  return (
    <div className="fixed top-4 right-4 z-[9999] flex flex-col gap-2 max-w-sm pointer-events-none">
      {toasts.map((t) => (
        <div
          key={t.id}
          className={`pointer-events-auto flex items-start gap-2 px-4 py-3 rounded-lg border shadow-lg text-sm font-medium animate-slide-in ${BG[t.type]}`}
          role="alert"
        >
          {ICON[t.type]}
          <span className="flex-1 leading-snug">{t.message}</span>
          <button onClick={() => dismiss(t.id)} className="opacity-60 hover:opacity-100 flex-shrink-0">
            <X className="w-3.5 h-3.5" />
          </button>
        </div>
      ))}
    </div>
  );
}
